import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiJson } from "../../utils/api.js";
import { fmtDt, userTitle } from "../../utils/users.js";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.js";
import { useMe } from "../../hooks/useMe.js";
import { ClientCardModal } from "../components/ClientCardModal.jsx";
import { CrmRow } from "../components/CrmRow.jsx";
import { MessengerBadge } from "../components/MessengerBadge.jsx";
import { FiltersModal } from "../components/FiltersModal.jsx";
import { FilterChips } from "../components/FilterChips.jsx";
import { ListEmpty } from "../components/ListEmpty.jsx";
import { PageReveal } from "../components/PageReveal.jsx";

const PAGE_SIZE = 30;

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = (parts[0] || "").slice(0, 1);
  const b = (parts[1] || "").slice(0, 1);
  return (a + b).toUpperCase();
}

export function ClientsPage() {
  const { isAdmin } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();

  const cardUserId = useMemo(() => {
    const c = searchParams.get("client");
    return c != null && c !== "" ? c : null;
  }, [searchParams]);
  const cardOpen = !!cardUserId;
  const openClientCard = useCallback(
    (userId) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (userId != null) p.set("client", String(userId));
          else p.delete("client");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const closeClientCard = useCallback(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("client");
        return p;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const segment = useMemo(() => {
    const s = (searchParams.get("segment") || "clients").toLowerCase();
    return s === "franchise" ? "franchise" : "clients";
  }, [searchParams]);

  const setSegment = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "franchise") p.set("segment", "franchise");
          else p.delete("segment");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const filtersOpen = useMemo(
    () => searchParams.get("filters") === "1",
    [searchParams]
  );
  const setFiltersOpen = useCallback(
    (open) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (open) p.set("filters", "1");
          else p.delete("filters");
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 300);

  const [sort, setSort] = useState("date");
  const [managerId, setManagerId] = useState("");
  const [tagId, setTagId] = useState("");

  const [managers, setManagers] = useState([]);
  const [tags, setTags] = useState([]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const query = useMemo(() => qDebounced.trim(), [qDebounced]);

  const listRef = useRef({ hasMore: true, loading: false });
  const containerRef = useRef(null);

  function buildParams(nextOffset, includeFilters = false) {
    const qs = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(nextOffset),
    });
    if (query) qs.set("q", query);
    if (sort) qs.set("sort", sort);
    if (isAdmin && managerId) qs.set("manager_id", managerId);
    if (tagId) qs.set("tag_id", tagId);
    if (includeFilters) qs.set("include_filters", "1");
    qs.set("client_kind", segment === "franchise" ? "franchise" : "client");
    return qs;
  }

  async function load({ reset }) {
    if (listRef.current.loading) return;
    listRef.current.loading = true;
    if (reset) setLoading(true);
    else setLoadingMore(true);

    try {
      const nextOffset = reset ? 0 : offset;
      const data = await apiJson(`/api/users?${buildParams(nextOffset, reset).toString()}`);
      const newItems = data.items || [];
      const newTotal = data.total || 0;
      setTotal(newTotal);
      if (reset) {
        setItems(newItems);
        setOffset(newItems.length);
        listRef.current.hasMore = newItems.length < newTotal;
        if (data.tags?.items) setTags(data.tags.items);
        if (data.managers?.items) setManagers((data.managers.items || []).filter((m) => m.is_active !== false));
      } else {
        setItems((prev) => [...prev, ...newItems]);
        setOffset((prev) => prev + newItems.length);
        listRef.current.hasMore = nextOffset + newItems.length < newTotal;
      }
    } catch {
      if (reset) {
        setItems([]);
        setTotal(0);
        setOffset(0);
        listRef.current.hasMore = false;
      }
    } finally {
      listRef.current.loading = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }

  useEffect(() => {
    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort, managerId, tagId, isAdmin, segment]);

  const hasActiveFilters = !!sort || !!managerId || !!tagId;
  const sortTitle = sort === "name" ? "По имени" : "По дате";
  const managerTitle = managerId ? managers.find((m) => String(m.id) === String(managerId))?.display_name || "Менеджер" : "";
  const tagTitle = tagId ? tags.find((t) => String(t.id) === String(tagId))?.title || "Тег" : "";
  const filterChips = [
    sort === "name" && { key: "sort", label: sortTitle, onRemove: () => setSort("date") },
    isAdmin && managerId && { key: "manager", label: `МОП: ${managerTitle}`, onRemove: () => setManagerId("") },
    tagId && { key: "tag", label: `Тег: ${tagTitle}`, onRemove: () => setTagId("") },
  ].filter(Boolean);

  const FilterIcon = (
    <svg className="crmIconSvg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );

  function isNearBottom(el, threshold = 180) {
    if (!el) return false;
    return el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }

  return (
    <>
      <main
        className="chat"
        id="clientsScreen"
      >
        <div className="crmStickyStack">
          <div className="reportsTabs dealsFunnelTabs">
            <button
              type="button"
              role="tab"
              aria-selected={segment === "clients"}
              className={`reportsTab ${segment === "clients" ? "active" : ""}`}
              onClick={() => setSegment("clients")}
            >
              Клиенты
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={segment === "franchise"}
              className={`reportsTab ${segment === "franchise" ? "active" : ""}`}
              onClick={() => setSegment("franchise")}
            >
              Франчайзи
            </button>
          </div>
          <div className="crmToolbar">
            <div className="crmToolbarRow">
              <input
                className="input crmSearchInput"
                id="q"
                placeholder="Поиск: имя, телефон или id"
                autoComplete="off"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button
                type="button"
                className={`crmIconBtn ${hasActiveFilters ? "isActive" : ""}`}
                title="Фильтры"
                aria-label="Фильтры"
                onClick={() => setFiltersOpen(true)}
              >
                {FilterIcon}
              </button>
            </div>
            {hasActiveFilters ? (
              <FilterChips chips={filterChips} />
            ) : null}
          </div>
        </div>

        <div className="section">
          <div
            className="crmListSurface"
            id="list"
            ref={containerRef}
            onScroll={() => {
              const el = containerRef.current;
              if (!el) return;
              if (listRef.current.hasMore && !listRef.current.loading && isNearBottom(el)) {
                load({ reset: false });
              }
            }}
          >
            <PageReveal ready={!loading}>
              {!items.length ? (
                <ListEmpty
                  message={
                    segment === "franchise"
                      ? "Нет франчайзи по фильтрам"
                      : "Нет клиентов по фильтрам"
                  }
                />
              ) : (
                <>
                  {items.map((u) => (
                    <CrmRow
                      key={String(u.id)}
                      barClass="good"
                      avatar={initialsFromName(userTitle(u))}
                      title={
                        <span className="crmRowTitleCluster">
                          <span className="crmRowTitleName">{userTitle(u)}</span>
                          <MessengerBadge messenger={u.messenger} />
                        </span>
                      }
                      right={fmtDt(u.first_contact_at)}
                      bottom={
                        <span className="crmMeta" title={u.username ? `@${u.username}` : undefined}>
                          {u.username ? `@${u.username}` : (String(u.phone || "").trim() || "")}
                        </span>
                      }
                      onClick={() => openClientCard(u.id)}
                    />
                  ))}
                </>
              )}
            </PageReveal>
          </div>
        </div>
      </main>

      <ClientCardModal
        open={cardOpen}
        onClose={closeClientCard}
        userId={cardUserId}
        useTelegramId={false}
      />
      <FiltersModal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <div className="dealsFiltersBody">
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Сортировка</label>
            <select
              className="dealsFilterSelect"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="date">По дате (новые)</option>
              <option value="name">По имени (А—Я)</option>
            </select>
          </div>
          {isAdmin ? (
            <div className="dealsFilterRow">
              <label className="dealsFilterLabel">Менеджер</label>
              <select
                className="dealsFilterSelect"
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
              >
                <option value="">Все менеджеры</option>
                {managers.map((m) => (
                  <option key={String(m.id)} value={String(m.id)}>
                    {m.display_name || m.code || `id=${m.id}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="dealsFilterRow">
            <label className="dealsFilterLabel">Тег</label>
            <select
              className="dealsFilterSelect"
              value={tagId}
              onChange={(e) => setTagId(e.target.value)}
            >
              <option value="">Все теги</option>
              {tags.map((t) => (
                <option key={String(t.id)} value={String(t.id)}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>
          <div className="dealsFilterActions">
            <button
              type="button"
              className="linkBtn"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => setFiltersOpen(false)}
            >
              Применить
            </button>
          </div>
        </div>
      </FiltersModal>
    </>
  );
}

